import { useState, useCallback, useRef, useEffect } from 'react';
import {
  isWeChatBrowser,
  isIOSDevice,
  getSignatureUrl,
  recordIOSEntryUrl,
  clearStoredEntryUrl,
  fetchJsSdkConfig,
  fetchOpenPlatformJsSdkConfig,
  type WeChatShareData,
} from '../utils/wechat';

/** JSSDK 签名来源：公众号应用 或 开放平台网站应用 */
export type WeChatJssdkSource = 'official_account' | 'open_platform';

export type UseWeChatShareOptions = {
  /** 默认 `official_account`（`/api/wechat/js-sdk/config`）；`open_platform` 使用网站应用签名 */
  jssdkSource?: WeChatJssdkSource;
};

/**
 * useWeChatShare 返回值
 */
export interface UseWeChatShareReturn {
  /** JSSDK 是否已配置就绪 */
  ready: boolean;
  /** 错误信息，无错误时为 null */
  error: string | null;
  /** 是否正在初始化 */
  loading: boolean;
  /** 是否在微信浏览器中 */
  isWeChat: boolean;
  /** 是否在 iOS 设备上 */
  isIOS: boolean;
  /** 初始化 JSSDK 配置 */
  init: (jsApiList?: string[], debug?: boolean) => Promise<boolean>;
  /** 设置分享信息（分享给朋友 + 朋友圈） */
  setShareInfo: (options: WeChatShareData) => void;
  /** 仅更新「发送给朋友」分享卡片内容（依赖右上角菜单触发分享） */
  setAppMessageShare: (options: WeChatShareData) => void;
  /** 仅更新「分享到朋友圈」分享内容 */
  setTimelineShare: (options: WeChatShareData) => void;
  /** 重置所有状态 */
  reset: () => void;
  /** 清除 iOS 入口 URL 缓存 */
  clearStoredUrl: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WxSdk = any;

/**
 * 微信分享 Hook
 *
 * 提供微信 JSSDK 分享功能的完整封装，包括：
 * 1. JSSDK 配置初始化（自动处理 iOS 签名 URL 问题）
 * 2. 分享给朋友 / 分享到朋友圈
 * 3. 状态管理（ready / loading / error）
 * 4. 非微信环境自动降级（静默跳过）
 *
 * 传入 `{ jssdkSource: 'open_platform' }` 时，签名走开放平台网站应用（与公众号 AppID 不同）。
 *
 * @example
 * ```tsx
 * function SharePage() {
 *   const { ready, loading, error, isWeChat, init, setShareInfo } = useWeChatShare();
 *
 *   useEffect(() => {
 *     init();
 *   }, [init]);
 *
 *   useEffect(() => {
 *     if (ready) {
 *       setShareInfo({
 *         title: '分享标题',
 *         desc: '分享描述',
 *         link: window.location.href,
 *         imgUrl: 'https://example.com/share-icon.png',
 *       });
 *     }
 *   }, [ready, setShareInfo]);
 *
 *   if (!isWeChat) return <div>请在微信中打开</div>;
 *   if (loading) return <div>初始化中...</div>;
 *   if (error) return <div>错误: {error}</div>;
 *   return <div>分享已配置</div>;
 * }
 * ```
 */
export function useWeChatShare(options?: UseWeChatShareOptions): UseWeChatShareReturn {
  const jssdkSource: WeChatJssdkSource = options?.jssdkSource ?? 'official_account';
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 用 ref 追踪 ready 状态，避免 setShareInfo 闭包捕获旧值
  const readyRef = useRef(false);
  // 缓存 wx SDK 实例，避免重复 import
  const wxRef = useRef<WxSdk>(null);

  const isWeChat = isWeChatBrowser();
  const isIOS = isIOSDevice();

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      readyRef.current = false;
    };
  }, []);

  /**
   * 初始化 JSSDK 配置
   *
   * @param jsApiList 需要使用的 JS 接口列表
   * @param debug 是否开启调试模式（微信会弹窗提示配置信息）
   * @returns 是否初始化成功
   */
  const init = useCallback(
    async (
      jsApiList: string[] = ['updateAppMessageShareData', 'updateTimelineShareData'],
      debug: boolean = false
    ): Promise<boolean> => {
      if (!isWeChatBrowser()) {
        console.log('[WeChat] 非微信环境，跳过 JSSDK 初始化');
        return false;
      }

      try {
        setLoading(true);
        setError(null);

        // 记录 iOS 入口 URL（仅首次）
        recordIOSEntryUrl();

        // 获取签名 URL
        const signatureUrl = getSignatureUrl();
        console.log('[WeChat] 签名 URL:', signatureUrl);

        // 从后端获取 JSSDK 配置（公众号 或 开放平台网站应用）
        const config =
          jssdkSource === 'open_platform'
            ? await fetchOpenPlatformJsSdkConfig(signatureUrl)
            : await fetchJsSdkConfig(signatureUrl);
        console.log('[WeChat] 获取配置成功:', {
          appId: config.appId,
          timestamp: config.timestamp,
          nonceStr: config.nonceStr,
        });

        // 动态加载 weixin-js-sdk（按需加载，非微信环境不会执行到这里）
        if (!wxRef.current) {
          const wxModule = await import('weixin-js-sdk');
          wxRef.current = wxModule.default || wxModule;
        }
        const wx = wxRef.current;

        // 配置 JSSDK
        wx.config({
          debug,
          appId: config.appId,
          timestamp: Number(config.timestamp),
          nonceStr: config.nonceStr,
          signature: config.signature,
          jsApiList,
        });

        // 等待配置结果
        return new Promise<boolean>((resolve) => {
          wx.ready(() => {
            console.log('[WeChat] JSSDK 配置成功');
            setReady(true);
            readyRef.current = true;
            setError(null);
            resolve(true);
          });

          wx.error((err: { errMsg?: string }) => {
            const errMsg = err?.errMsg || 'JSSDK 配置失败';
            console.error('[WeChat] JSSDK 配置失败:', err);
            setError(errMsg);
            setReady(false);
            readyRef.current = false;
            resolve(false);
          });
        });
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : '初始化失败';
        console.error('[WeChat] 初始化失败:', e);
        setError(message);
        setReady(false);
        readyRef.current = false;
        return false;
      } finally {
        setLoading(false);
      }
    },
    [jssdkSource]
  );

  /**
   * 设置分享信息
   *
   * 同时配置"分享给朋友"和"分享到朋友圈"。
   * 必须在 init() 成功（ready === true）后调用。
   */
  const setShareInfo = useCallback((options: WeChatShareData) => {
    if (!readyRef.current || !wxRef.current) {
      console.warn('[WeChat] JSSDK 未就绪，无法设置分享信息');
      return;
    }

    const wx = wxRef.current;
    const shareData = {
      title: options.title,
      desc: options.desc || '',
      link: options.link || window.location.href.split('#')[0],
      imgUrl: options.imgUrl || '',
    };

    console.log('[WeChat] 设置分享信息:', shareData);

    // 分享给朋友
    wx.updateAppMessageShareData({
      title: shareData.title,
      desc: shareData.desc,
      link: shareData.link,
      imgUrl: shareData.imgUrl,
      success: () => {
        console.log('[WeChat] 分享给朋友配置成功');
      },
      fail: (err: { errMsg?: string }) => {
        console.error('[WeChat] 分享给朋友配置失败:', err);
      },
    });

    // 分享到朋友圈
    wx.updateTimelineShareData({
      title: shareData.title,
      link: shareData.link,
      imgUrl: shareData.imgUrl,
      success: () => {
        console.log('[WeChat] 分享到朋友圈配置成功');
      },
      fail: (err: { errMsg?: string }) => {
        console.error('[WeChat] 分享到朋友圈配置失败:', err);
      },
    });
  }, []);

  const setAppMessageShare = useCallback((options: WeChatShareData) => {
    if (!readyRef.current || !wxRef.current) {
      console.warn('[WeChat] JSSDK 未就绪，无法设置「发送给朋友」分享信息');
      return;
    }
    const wx = wxRef.current;
    const link = options.link || window.location.href.split('#')[0];
    const payload = {
      title: options.title,
      desc: options.desc || '',
      link,
      imgUrl: options.imgUrl || '',
    };
    wx.updateAppMessageShareData({
      ...payload,
      success: () => {
        console.log('[WeChat] 分享给朋友配置成功');
      },
      fail: (err: { errMsg?: string }) => {
        console.error('[WeChat] 分享给朋友配置失败:', err);
      },
    });
  }, []);

  const setTimelineShare = useCallback((options: WeChatShareData) => {
    if (!readyRef.current || !wxRef.current) {
      console.warn('[WeChat] JSSDK 未就绪，无法设置「朋友圈」分享信息');
      return;
    }
    const wx = wxRef.current;
    const link = options.link || window.location.href.split('#')[0];
    wx.updateTimelineShareData({
      title: options.title,
      link,
      imgUrl: options.imgUrl || '',
      success: () => {
        console.log('[WeChat] 分享到朋友圈配置成功');
      },
      fail: (err: { errMsg?: string }) => {
        console.error('[WeChat] 分享到朋友圈配置失败:', err);
      },
    });
  }, []);

  /**
   * 重置所有状态
   */
  const reset = useCallback(() => {
    setReady(false);
    setError(null);
    setLoading(false);
    readyRef.current = false;
  }, []);

  /**
   * 清除 iOS 入口 URL 缓存
   */
  const clearStoredUrl = useCallback(() => {
    clearStoredEntryUrl();
  }, []);

  return {
    ready,
    error,
    loading,
    isWeChat,
    isIOS,
    init,
    setShareInfo,
    setAppMessageShare,
    setTimelineShare,
    reset,
    clearStoredUrl,
  };
}
